/**
 * Servicio para gestión de evidencias fotográficas
 * 
 * Funciones:
 * - Subir imágenes (una o múltiples)
 * - Obtener evidencias por auditoría
 * - Obtener evidencia específica
 * - Eliminar evidencia
 * - Obtener estadísticas
 */

import api from './api';

export interface Evidencia {
  id: number;
  auditoria_id: number;
  pregunta_id: number | null;
  url: string;
  nombre_archivo: string;
  tipo_mime: string;
  tamano: number;
  descripcion: string | null;
  orden: number;
  fecha_subida: string;
  subido_por: number;
  activo: boolean;
  // Campos joined
  subido_por_nombre?: string;
  pregunta_texto?: string;
}

export interface EvidenciasResponse {
  auditoria_id: number;
  total: number;
  evidencias: Evidencia[];
}

export interface EvidenciaStats {
  total_evidencias: number;
  total_tamano: number;
  preguntas_con_evidencias: number;
  ultima_subida: string | null;
}

export interface UploadEvidenciaRequest {
  auditoria_id: number;
  pregunta_id?: number;
  descripcion?: string;
  imagenes: File[];
}

export interface UploadEvidenciaResponse {
  message: string;
  evidencias: Evidencia[];
}

const evidenciasService = {
  /**
   * Subir una o más imágenes como evidencias
   * @param data Datos del upload incluyendo archivos
   * @returns Promise con las evidencias creadas
   */
  async upload(data: UploadEvidenciaRequest): Promise<UploadEvidenciaResponse> {
    const formData = new FormData();
    
    formData.append('auditoria_id', data.auditoria_id.toString());
    
    if (data.pregunta_id) {
      formData.append('pregunta_id', data.pregunta_id.toString());
    }
    
    if (data.descripcion) {
      formData.append('descripcion', data.descripcion);
    }
    
    // Agregar todos los archivos con el mismo nombre de campo
    data.imagenes.forEach(imagen => {
      formData.append('imagenes', imagen);
    });

    const response = await api.post<UploadEvidenciaResponse>('/evidencias', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  /**
   * Obtener todas las evidencias de una auditoría
   * @param auditoriaId ID de la auditoría
   * @returns Promise con lista de evidencias
   */
  async getByAuditoria(auditoriaId: number): Promise<EvidenciasResponse> {
    const response = await api.get<EvidenciasResponse>(`/evidencias/auditoria/${auditoriaId}`);
    return response.data;
  },

  /**
   * Obtener una evidencia específica por ID
   * @param id ID de la evidencia
   * @returns Promise con la evidencia
   */
  async getById(id: number): Promise<Evidencia> {
    const response = await api.get<Evidencia>(`/evidencias/${id}`);
    return response.data;
  },

  /**
   * Eliminar una evidencia (soft delete)
   * @param id ID de la evidencia a eliminar
   * @returns Promise con mensaje de confirmación
   */
  async delete(id: number): Promise<{ message: string; evidencia_id: number }> {
    const response = await api.delete(`/evidencias/${id}`);
    return response.data;
  },

  /**
   * Obtener estadísticas de evidencias de una auditoría
   * @param auditoriaId ID de la auditoría
   * @returns Promise con estadísticas
   */
  async getStats(auditoriaId: number): Promise<EvidenciaStats> {
    const response = await api.get<EvidenciaStats>(`/evidencias/auditoria/${auditoriaId}/stats`);
    return response.data;
  },

  /**
   * Construir URL completa de una evidencia para mostrar en <img>
   * @param evidencia Evidencia o URL relativa
   * @returns URL completa
   */
  getImageUrl(evidencia: Evidencia | string): string {
    const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5001';
    const url = typeof evidencia === 'string' ? evidencia : evidencia.url;
    
    // Si ya es URL completa, retornar tal cual
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Construir URL completa
    return `${baseURL}${url}`;
  },

  /**
   * Formatear tamaño de archivo en formato legible
   * @param bytes Tamaño en bytes
   * @returns String formateado (ej: "2.5 MB")
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  },

  /**
   * Validar archivo antes de subir
   * @param file Archivo a validar
   * @returns Objeto con validez y mensaje de error
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    // Validar tipo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Tipo de archivo no permitido: ${file.type}. Solo se aceptan imágenes (JPEG, PNG, WebP, GIF).`
      };
    }

    // Validar tamaño (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        valid: false,
        error: `Archivo demasiado grande: ${this.formatFileSize(file.size)}. Máximo permitido: 5 MB.`
      };
    }

    return { valid: true };
  },

  /**
   * Comprimir imagen en el cliente antes de subir (opcional)
   * @param file Archivo original
   * @param maxWidth Ancho máximo
   * @param maxHeight Alto máximo
   * @param quality Calidad de compresión (0-1)
   * @returns Promise con archivo comprimido
   */
  async compressImage(
    file: File, 
    maxWidth: number = 1200, 
    maxHeight: number = 1200,
    quality: number = 0.8
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calcular nuevas dimensiones manteniendo aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = height * (maxWidth / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = width * (maxHeight / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('No se pudo obtener contexto del canvas'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          // Convertir canvas a blob
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('No se pudo comprimir la imagen'));
                return;
              }

              // Crear nuevo File con el blob comprimido
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });

              resolve(compressedFile);
            },
            file.type,
            quality
          );
        };

        img.onerror = () => reject(new Error('Error al cargar la imagen'));
        img.src = e.target?.result as string;
      };

      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsDataURL(file);
    });
  }
};

export default evidenciasService;
